import { ClickAwayListener, Popper } from "@mui/base";
import clsx from "clsx";
import React, { useState } from "react";

import { Icon } from "~/assets/icons/Icon";
import { ThemePreference } from "~/contexts/ThemeContext";

import { useTheme } from "../../contexts/ThemeContext";
import styles from "./ThemeToggleCompact.module.scss";

type Props = {
  isSidebarOpen: boolean;
  className?: string;
};

const ICONS: Record<ThemePreference, React.ReactNode> = {
  system: <Icon iconName="ThemeControl" />,
  light: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="#A4A4A4"
    >
      <path d="M10 15a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z" fill="#A4A4A4" />
      <path
        d="M10 1v2M10 17v2M18.66 4.66l-1.41 1.41M4.75 15.25l-1.41 1.41M19 10h-2M3 10H1M18.66 15.34l-1.41-1.41M4.75 4.75 3.34 3.34"
        stroke="#A4A4A4"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  dark: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="#A4A4A4"
    >
      <path
        d="M17.293 13.293A8 8 0 0 1 6.707 2.707a8.001 8.001 0 1 0 10.586 10.586Z"
        fill="#A4A4A4"
      />
    </svg>
  ),
};

const OPTIONS: Array<{
  key: ThemePreference;
  label: string;
}> = [
  { key: "system", label: "System" },
  { key: "light", label: "Light" },
  { key: "dark", label: "Dark" },
];

const ThemeToggleCompact = ({ isSidebarOpen, className }: Props) => {
  const { themePreference, setThemePreference } = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);
  const closeDropdown = () => setAnchorEl(null);
  const handlePick = (pref: typeof themePreference) => {
    setThemePreference(pref);
    closeDropdown();
  };
  const currentIcon = ICONS[themePreference];

  // === OPEN SIDEBAR: show 3 inline buttons, no popper ===
  if (isSidebarOpen) {
    return (
      <div className={clsx(styles.inlineWrap, className)}>
        <div className={styles.inlineButtons}>
          {OPTIONS.map((opt) => (
            <div className={styles.themeToggleInlineWrapper} key={opt.key}>
              <button
                type="button"
                className={clsx(styles.themeToggleInlineBtn, {
                  [styles.inlineBtnActive]: themePreference === opt.key,
                })}
                onClick={() => setThemePreference(opt.key)}
              >
                <span className={styles.inlineIcon}>{ICONS[opt.key]}</span>
              </button>
              {opt.key === themePreference && (
                <span className={styles.activeLabel}>{opt.label}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // === CLOSED SIDEBAR: one icon + dropdown ===
  return (
    <>
      <div
        className={clsx(styles.closedTrigger, className)}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <span className={styles.closedIcon}>{currentIcon}</span>
      </div>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="right"
        slotProps={{
          root: {
            className: styles.popperRoot,
          },
        }}
      >
        <ClickAwayListener onClickAway={closeDropdown}>
          <div className={styles.dropdown}>
            {OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={clsx(styles.dropdownBtn, {
                  [styles.dropdownBtnActive]: themePreference === opt.key,
                })}
                onClick={() => handlePick(opt.key)}
              >
                <span className={styles.dropdownIcon}>{ICONS[opt.key]}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default ThemeToggleCompact;
